// GAVION A10: « Changer le mot de passe » (Profil) changes the password in place with the signed-in session
// (supabase.auth.updateUser), like the activation page does. The upstream `update_password` edge function sent a
// GoTrue reset email instead: the app has no Auth SMTP (INVITE_DELIVERY=link) and one-time email links are eaten
// by link scanners, so that path never worked here and is no longer called.
import type { SupabaseClient } from "@supabase/supabase-js";

export const MIN_PASSWORD_LENGTH = 8;

export type PasswordErrorCode =
  | "too_short"
  | "same_password"
  | "weak_password"
  | "reauthentication_needed"
  | "session_expired"
  | "unknown";

const AUTH_CODES: Record<string, PasswordErrorCode> = {
  same_password: "same_password",
  weak_password: "weak_password",
  reauthentication_needed: "reauthentication_needed",
  reauth_nonce_missing: "reauthentication_needed",
  session_not_found: "session_expired",
  session_expired: "session_expired",
};

const fail = (code: PasswordErrorCode) =>
  Object.assign(new Error(code), { code });

export const passwordErrorCode = (e: unknown): PasswordErrorCode => {
  const code = (e as { code?: unknown } | null)?.code;
  return typeof code === "string" &&
    (Object.values(AUTH_CODES) as string[]).concat("too_short").includes(code)
    ? (code as PasswordErrorCode)
    : "unknown";
};

const checkLength = (password: string) => {
  if (typeof password !== "string" || password.length < MIN_PASSWORD_LENGTH) {
    throw fail("too_short");
  }
};

export type PasswordMethods = {
  /** Sets a new password for the signed-in user. Rejects with `{ code: PasswordErrorCode }`. */
  changeOwnPassword(password: string): Promise<void>;
  /** False for a Microsoft-only account (no email/password identity): the Profile hides the button. */
  hasPasswordSignIn(): Promise<boolean>;
};

export const createSupabasePasswordMethods = (
  getSupabaseClient: () => SupabaseClient,
): PasswordMethods => ({
  async changeOwnPassword(password) {
    checkLength(password);
    const { error } = await getSupabaseClient().auth.updateUser({ password });
    if (error) {
      const raw = (error as { code?: string }).code ?? "";
      throw fail(AUTH_CODES[raw] ?? "unknown");
    }
  },
  async hasPasswordSignIn() {
    const { data, error } = await getSupabaseClient().auth.getUser();
    const meta = data?.user?.app_metadata;
    // Unknown state keeps the button: updateUser still works for any signed-in user.
    if (error || !meta) return true;
    const providers: unknown[] = Array.isArray(meta.providers)
      ? meta.providers
      : [meta.provider];
    return providers.includes("email");
  },
});

/** Demo (FakeRest): validates like the real one, stores nothing. */
export const createFakePasswordMethods = (): PasswordMethods => ({
  async changeOwnPassword(password) {
    checkLength(password);
  },
  async hasPasswordSignIn() {
    return true;
  },
});
